import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/apiService';

const VendorTransactions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { vendorId } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('All');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        vendorId: vendorId,
        search: searchKeyword,
        payStatus: paymentStatus
      };
      
      const response = await apiService.post('vendorTransactionList', params);
      if (response && response.data) {
        setTransactions(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load transactions');
      console.error('Transactions fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, vendorId, searchKeyword, paymentStatus]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTransactions();
    }, searchKeyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchTransactions]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setPaymentStatus(value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        {/* Breadcrumb */}
        <nav className="flex mb-0" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 text-xs">
            <li className="inline-flex items-center">
              <a href="/dashboard" className="text-gray-600 hover:text-[#009ef7]">Home</a>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <a href="/caller-vendors" className="text-gray-600 hover:text-[#009ef7]">Vendor</a>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <span className="text-gray-500">Transaction List</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Transaction List</h1>
              <button
                onClick={() => navigate(`/createTransaction/${vendorId}`)}
                className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 transition-colors flex items-center"
              >
                <i className="bi bi-plus mr-2"></i>
                Create Transaction
              </button>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              {/* Search */}
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Search by paymentId OrderId here.."
                  value={searchKeyword}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              {/* Payment Status Filter */}
              <div>
                <select
                  value={paymentStatus}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                >
                  <option value="All">Show All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Complete</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-48">Transaction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Credit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-48">Total Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Created At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction, index) => (
                    <tr key={transaction._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {((currentPage - 1) * recordsPerPage) + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">Payment ID: {transaction.paymentId}</div>
                          <div className="text-gray-500">Order ID: {transaction.orderId}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.credit || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{transaction.amount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.createdAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata'
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorTransactions;